@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginetabular

import org.example.project.dashboard.AppDialogs
import org.example.project.dashboard.LocalScreenReload

import androidx.compose.runtime.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.example.project.service.FrameworkUserSettingsService
import org.example.project.service.GlobalManager
import org.example.project.service.RollenService
import org.example.project.utils.All
import kotlin.collections.get

private const val MAX_VISIBLE_ROWS = 2000
private const val EXTERNAL_DATA_SYNC_DELAY_MS = 180L
private const val TABULAR_FILTER_SETTING_PREFIX = "tabular.filter."

private data class TabularViewState(
    val renderedData: List<Pair<String, List<String>>>,
    val filteredDataSize: Int
)

data class TabularToolbarButton(
    val label: String,
    val icon: String,
    val enabled: Boolean = true,
    val onClick: () -> Unit = {},
    val dropdownOptions: List<Pair<String, String>> = emptyList(),
    val selectedDropdownValue: String? = null,
    val dropdownPlaceholder: String = label,
    val onDropdownChange: ((String?) -> Unit)? = null
)

data class TabularBulkEditRequest(
    val nonce: Int,
    val rowIds: Set<String>,
    val column: String,
    val value: String
)

private fun tabularFilterMatches(cellValue: String, filterValue: String): Boolean {
    val f = filterValue.trim()
    if (f.isEmpty()) return true
    val cell = cellValue.trim()
    val operators = listOf(">=", "<=", ">", "<")
    val operator = operators.firstOrNull { f.startsWith(it) }
        ?: return cell.contains(f, ignoreCase = true)
    val expected = f.removePrefix(operator).trim().replace(',', '.').toDoubleOrNull() ?: return false
    val actual = cell.replace(',', '.').toDoubleOrNull() ?: return false
    return when (operator) {
        ">" -> actual > expected
        "<" -> actual < expected
        ">=" -> actual >= expected
        "<=" -> actual <= expected
        else -> false
    }
}

/**
 * TabularScreen — Engine mit striktem Commit-Modell.
 *
 * Regeln:
 *  - NICHTS wird gespeichert bis "Speichern" gedrückt wird.
 *  - Alle Änderungen (Edit, Create, Delete, Toggle, CSV) leben im RAM.
 *  - Erst onCommit(shownData) schreibt raus, danach sendet die Engine ID-basierte Entity-Events.
 *  - Callback vorhanden = Button sichtbar. Null = kein Button.
 *  - onCellClick als Domain-Callback existiert nicht.
 *    Toggles (✅/⬜) erkennt die Engine intern und behandelt sie als Cell-Edit im RAM.
 *  - Der Screen weiß nichts über Speicherzeitpunkt oder Button-Sichtbarkeit.
 *
 * Pflicht:   headers, data, onCommit
 * Optional:  alles andere
 */
@Composable
fun TabularScreen(
    headers: List<String>,
    headerDisplayLabels: List<String> = headers,
    data: List<Pair<String, List<String>>>,
    onCommit: (List<Pair<String, List<String>>>) -> Unit,
    onCommitConfirmed: ((List<Pair<String, List<String>>>, (Boolean) -> Unit) -> Unit)? = null,
    models: List<All> = emptyList(),

    screenKey: String = "",
    entityScope: String = "",
    title: String? = null,
    rollenKey: String = "",

    editable: Boolean = true,
    commitChangedRowsOnly: Boolean = false,
    selectionMode: String = TabularSelectionMode.ROWS,
    createButtonLabel: String = "Erstellen",
    snapButtonLabel: String = "Snap hinzufügen",
    saveButtonLabel: String = "Speichern",
    importButtonLabel: String = "Import",
    exportButtonLabel: String = "Export",
    exportFileName: String? = null,
    csvImportAccept: String = ".csv",
    toolbarMode: String = TabularToolbarMode.DEFAULT,
    nonEditableCols: Set<String> = emptySet(),
    // Server-confirmed action results may update these columns without discarding local edits.
    externalUpdateColumns: Set<String> = emptySet(),
    // A screen can opt into immediate row synchronization for actions that move rows away.
    syncExternalRowsWhileDirty: Boolean = false,
    externalDataSyncDelayMs: Long = EXTERNAL_DATA_SYNC_DELAY_MS,
    toggleColumns: Set<String> = emptySet(),
    dropdownOptions: Map<String, List<String>> = emptyMap(),
    rowDropdownOptions: ((rowId: String, row: List<String>, colIndex: Int) -> List<String>?)? = null,
    calendarColumns: Set<String> = emptySet(),
    multilineColumns: Set<String> = emptySet(),
    cellBackgroundColors: Map<Pair<String, Int>, CellColor> = emptyMap(),

    // Navigation - kein Speichern, nur UI-Reaktion
    onRefresh: (() -> Unit)? = null,
    onSearch: (() -> Unit)? = null,
    onSearchWithFilters: ((Map<String, String>) -> Unit)? = null,
    onRowClick: ((String) -> Unit)? = null,
    onSelectedRowsChange: ((Set<String>) -> Unit)? = null,
    onRowDoubleClick: ((String) -> Unit)? = null,
    selectedRowIndices: Set<Int> = emptySet(),
    onRowIndexSelect: ((Int, Boolean, Boolean) -> Unit)? = null,
    onRowIndexContextMenu: ((Int, Double, Double) -> Unit)? = null,
    onCellClick: ((String, Int) -> Unit)? = null,
    selectedCell: Pair<String, Int>? = null,
    selectedCellRange: TabularCellRange? = null,
    onCellSelectionChange: ((TabularCellRange) -> Unit)? = null,
    onCellContextMenu: ((String, Int, Double, Double) -> Unit)? = null,
    onHeaderClick: ((Int) -> Unit)? = null,
    onHeaderLabelEdit: ((Int, String) -> Unit)? = null,
    onCellEditTransform: ((String, String, String, List<String>) -> List<String>)? = null,
    cellEditable: (rowId: String, column: String, value: String) -> Boolean = { _, _, _ -> true },
    onCellChanged: ((String, String, String) -> Unit)? = null,
    onDraftChange: ((List<Pair<String, List<String>>>) -> Unit)? = null,
    bulkEditRequest: TabularBulkEditRequest? = null,
    onCreateAction: (() -> Unit)? = null,
    onSnapAction: (() -> Unit)? = null,
    onSaveRequest: (((() -> Unit)) -> Unit)? = null,
    onCsvImportRequest: ((String) -> Unit)? = null,

    // Sofort-Löschen - wenn angegeben, werden Zeilen direkt vom Server gelöscht (keine Save nötig)
    onDeleteRows: (suspend (List<String>) -> Unit)? = null,
    isDeleteEnabled: (Set<String>) -> Boolean = { it.isNotEmpty() },

    // Domain-Buttons - nur wenn der Screen sie braucht
    onApproveSelected: ((Set<String>) -> Unit)? = null,
    onInstallSelected: ((Set<String>) -> Unit)? = null,
    isInstallEnabled: (Set<String>) -> Boolean = { it.isNotEmpty() },
    onCancelRows: ((Set<String>) -> Unit)? = null,
    isCancelEnabled: (Set<String>) -> Boolean = { it.isNotEmpty() },
    onGenerate: (() -> Unit)? = null,
    approveButtonLabel: String = "Kontrolliert",
    installButtonLabel: String = "Installieren",
    cancelButtonLabel: String = "Stornieren",
    cancelButtonIcon: String = "images/close.svg",
    onResetPassword: ((Set<String>) -> Unit)? = null,
    generateButtonLabel: String = "Start",
    extraNavButtons: List<TabularToolbarButton> = emptyList(),
    extraButtons: (@Composable () -> Unit)? = null,

    // Override: wie sieht eine neue Zeile aus? Default: ID in Spalte 0, Rest leer
    createRow: (newId: String) -> List<String> = { newId ->
        MutableList(headers.size) { idx -> if (idx == 0) newId else "" }
    },

    // false = kein "Neu"-Button (z.B. Admin-only-Edit-Screens)
    allowCreate: Boolean = true,
    allowSave: Boolean = true,
    allowDelete: Boolean = true,
    allowImportButton: Boolean = true,
    allowExportButton: Boolean = true,
    allowSettingsButton: Boolean = true,
    allowRefreshButton: Boolean = true,
    allowSearchButton: Boolean = true,
    showToolbarExtras: Boolean = true,
    showFilterRow: Boolean = true,
    showFillerRows: Boolean = true,
    smartMode: Boolean = false,
    dataColumnWidthCss: String? = null,
    verticalScroll: Boolean = true,

    // true (default) = Standard-Metaspalten automatisch anhängen und befüllen
    includeMetadata: Boolean = true,
    hardReadOnly: Boolean = false,
    useUserSettings: Boolean = true,
    fixedLeadingColumns: List<String> = emptyList(),
    fixedTrailingColumns: List<String> = emptyList()
) {
    val scope = rememberCoroutineScope()
    val reloadScreen = LocalScreenReload.current
    val userSettingsVersion = FrameworkUserSettingsService.currentUserSettingsVersion()
    val ownHeaders = remember(headers, includeMetadata) {
        if (!includeMetadata) headers else headers.filterNot { it in META_HEADERS }
    }
    val ownHeaderCount = ownHeaders.size
    val sourceContainsMetaHeaders = remember(headers, includeMetadata) {
        includeMetadata && headers.any { it in META_HEADERS }
    }
    val sourceMetaIndexByName = remember(headers, includeMetadata) {
        if (!includeMetadata) emptyMap()
        else META_HEADERS.associateWith { meta -> headers.indexOf(meta) }
    }
    val sourceBaseIndices = remember(headers, includeMetadata) {
        if (!includeMetadata) headers.indices.toList()
        else headers.indices.filter { headers[it] !in META_HEADERS }
    }
    var resolvedModelsById by remember(screenKey) { mutableStateOf<Map<String, All>>(emptyMap()) }

    val extHeaders = remember(ownHeaders, includeMetadata) {
        if (includeMetadata) ownHeaders + META_HEADERS else ownHeaders
    }
    val headerLabelMap = remember(extHeaders, headerDisplayLabels, includeMetadata) {
        val ownLabels = if (!includeMetadata) {
            headerDisplayLabels
        } else {
            ownHeaders.mapIndexed { index, _ -> headerDisplayLabels.getOrNull(index).orEmpty() } + META_HEADERS
        }
        extHeaders.mapIndexed { index, header ->
            header to ownLabels.getOrNull(index).orEmpty()
        }.toMap()
    }
    fun normalizeIncomingRow(id: String, row: List<String>): MutableList<String> {
        if (!includeMetadata) {
            return row.toMutableList().apply {
                while (size < extHeaders.size) add("")
            }
        }
        val base = sourceBaseIndices.map { idx -> row.getOrNull(idx).orEmpty() }
        val model = resolvedModelsById[id]
        val rowMeta = if (!sourceContainsMetaHeaders && row.size >= ownHeaderCount + META_COUNT) {
            row.drop(ownHeaderCount).take(META_COUNT)
        } else {
            emptyList()
        }
        val meta = META_HEADERS.mapIndexed { metaIndex, metaHeader ->
            val sourceIndex = sourceMetaIndexByName[metaHeader] ?: -1
            when {
                sourceIndex in row.indices -> row[sourceIndex]
                rowMeta.getOrNull(metaIndex).orEmpty().isNotBlank() -> rowMeta.getOrNull(metaIndex).orEmpty()
                metaIndex == 0 -> model?.erstelltVon.orEmpty()
                metaIndex == 1 -> model?.erstelltAm.orEmpty()
                metaIndex == 2 -> model?.erstelltUm.orEmpty()
                metaIndex == 3 -> model?.geaendertVon.orEmpty()
                metaIndex == 4 -> model?.geaendertAm.orEmpty()
                metaIndex == 5 -> model?.geaendertUm.orEmpty()
                else -> ""
            }
        }
        return (base + meta).toMutableList().apply {
            while (size < extHeaders.size) add("")
        }
    }

    // ===================== RAM STATE =====================
    var shownHeaders by remember { mutableStateOf(extHeaders) }
    var shownData by remember(screenKey, entityScope) {
        mutableStateOf(data.map { (id, row) -> id to normalizeIncomingRow(id, row) })
    }
    var committedSnapshot by remember(screenKey, entityScope) { mutableStateOf(shownData) }
    var isDirty by remember(screenKey, entityScope) { mutableStateOf(false) }

    var selectedRows by remember(screenKey, entityScope) { mutableStateOf(setOf<String>()) }
    var lastClickedRowIndex by remember(screenKey, entityScope) { mutableStateOf<Int?>(null) }

    LaunchedEffect(selectedRows) {
        onSelectedRowsChange?.invoke(selectedRows)
    }

    var columnVisibility by remember(screenKey) { mutableStateOf(mutableMapOf<String, Boolean>()) }
    var columnOrder by remember(screenKey) { mutableStateOf(shownHeaders) }
    var showColumnToggle by remember(screenKey) { mutableStateOf(false) }

    var sortColumn by remember(screenKey) { mutableStateOf<String?>(null) }
    var sortAscending by remember(screenKey) { mutableStateOf(true) }

    val filterTexts = remember(screenKey, shownHeaders) { shownHeaders.map { mutableStateOf("") } }
    var visibleRowsCount by remember(screenKey) { mutableStateOf(MAX_VISIBLE_ROWS) }
    var csvImportProfiles by remember(screenKey) { mutableStateOf<Map<String, Map<String, String>>>(emptyMap()) }
    var selectedCsvImportProfile by remember(screenKey) { mutableStateOf<String?>(null) }
    var csvExportProfiles by remember(screenKey) { mutableStateOf<Map<String, Map<String, String>>>(emptyMap()) }
    var selectedCsvExportProfile by remember(screenKey) { mutableStateOf<String?>(null) }
    var filterProfiles by remember(screenKey) { mutableStateOf<Map<String, Map<String, String>>>(emptyMap()) }
    var selectedFilterProfile by remember(screenKey) { mutableStateOf<String?>(null) }
    var recoveredHiddenColumns by remember(screenKey) { mutableStateOf(false) }
    var recoveredEmptyFilters by remember(screenKey) { mutableStateOf(false) }
    val step = 1000

    // ===================== MAPS =====================
    val headerNameToIndex = remember(shownHeaders) {
        shownHeaders.mapIndexed { i, h -> h to i }.toMap()
    }
    val nonEditableColIndices = remember(nonEditableCols, headerNameToIndex) {
        val fromScreen = nonEditableCols.mapNotNull { headerNameToIndex[it] }.toSet()
        val metaIndices = if (includeMetadata) (ownHeaderCount until ownHeaderCount + META_COUNT).toSet() else emptySet()
        fromScreen + metaIndices
    }
    val toggleColIndices = remember(toggleColumns, headerNameToIndex) {
        toggleColumns.mapNotNull { headerNameToIndex[it] }.toSet()
    }
    val dropdownOptionIndices = remember(dropdownOptions, headerNameToIndex) {
        dropdownOptions.mapNotNull { (name, opts) ->
            headerNameToIndex[name]?.let { idx -> idx to opts }
        }.toMap()
    }
    val calendarColumnIndices = remember(calendarColumns, headerNameToIndex) {
        calendarColumns.mapNotNull { name -> headerNameToIndex[name] }.toSet()
    }
    val visibleColumns by derivedStateOf {
        columnOrder.filter { columnVisibility[it] ?: true }
    }
    // Filter directly against the input state. The table therefore reacts to every
    // keystroke and never requires the search button for already loaded rows.
    val activeFilterTexts = filterTexts
    fun rowMatchesActiveFilters(row: List<String>): Boolean {
        val n = minOf(row.size, activeFilterTexts.size)
        return (0 until n).all { i ->
            val f = activeFilterTexts[i].value.trim()
            val cellValue = row.getOrNull(i).orEmpty()
            when {
                f.isEmpty() -> true
                i in toggleColIndices -> {
                    when (f.lowercase()) {
                        "true" -> cellValue == "\u2705" || cellValue.equals("true", ignoreCase = true)
                        "false" -> cellValue == "\u2B1C" || cellValue.equals("false", ignoreCase = true)
                        else -> tabularFilterMatches(cellValue, f)
                    }
                }
                else -> tabularFilterMatches(cellValue, f)
            }
        }
    }
    val tabularView by derivedStateOf {
        val limit = visibleRowsCount.coerceAtLeast(1)
        if (sortColumn != null) {
            val colIdx = headerNameToIndex[sortColumn] ?: 0
            val filtered = shownData.filter { (_, row) -> rowMatchesActiveFilters(row) }
            val sorted = filtered.sortedWith(Comparator { a, b ->
                val valA = a.second.getOrElse(colIdx) { "" }
                val valB = b.second.getOrElse(colIdx) { "" }
                val numA = valA.toDoubleOrNull()
                val numB = valB.toDoubleOrNull()
                val cmp = if (numA != null && numB != null) numA.compareTo(numB)
                          else valA.compareTo(valB, ignoreCase = true)
                if (sortAscending) cmp else -cmp
            })
            TabularViewState(
                renderedData = sorted.take(limit),
                filteredDataSize = filtered.size
            )
        } else {
            var matches = 0
            val visible = ArrayList<Pair<String, List<String>>>(limit.coerceAtMost(shownData.size))
            shownData.forEach { row ->
                if (rowMatchesActiveFilters(row.second)) {
                    matches += 1
                    if (visible.size < limit) {
                        visible.add(row)
                    }
                }
            }
            TabularViewState(
                renderedData = visible,
                filteredDataSize = matches
            )
        }
    }
    val renderedData = tabularView.renderedData
    val filteredDataSize = tabularView.filteredDataSize

    // ===================== SYNC EXTERNAL DATA =====================
    LaunchedEffect(screenKey, models, data, includeMetadata) {
        resolvedModelsById = if (!includeMetadata) {
            emptyMap()
        } else {
            models.associateBy { it.id }
        }
    }
    LaunchedEffect(headers, data, resolvedModelsById, externalUpdateColumns, syncExternalRowsWhileDirty, externalDataSyncDelayMs) {
        if (shownData.isNotEmpty() && externalDataSyncDelayMs > 0L) {
            delay(externalDataSyncDelayMs)
        }
        val headersChanged = shownHeaders != extHeaders
        val incomingData = data.map { (id, row) -> id to normalizeIncomingRow(id, row) }
        if (isDirty) {
            if (syncExternalRowsWhileDirty) {
                val editedById = shownData.associateBy { it.first }
                val originalById = committedSnapshot.associateBy { it.first }
                shownData = incomingData.map { editedById[it.first] ?: it }
                committedSnapshot = incomingData.map { originalById[it.first] ?: it }
                val availableRowIds = incomingData.map { it.first }.toSet()
                selectedRows = selectedRows.filter { it in availableRowIds }.toSet()
            }
            if (!headersChanged && externalUpdateColumns.isNotEmpty()) {
                val indices = (externalUpdateColumns + META_HEADERS).mapNotNull { headerNameToIndex[it] }.toSet()
                shownData = mergeTabularExternalColumns(shownData, incomingData, indices)
                committedSnapshot = mergeTabularExternalColumns(committedSnapshot, incomingData, indices)
            }
            return@LaunchedEffect
        }
        shownHeaders = extHeaders
        shownData = if (headersChanged) {
            incomingData
        } else {
            val currentById = shownData.associateBy { it.first }
            incomingData.map { incoming ->
                val current = currentById[incoming.first]
                if (current != null && current.second == incoming.second) current else incoming
            }
        }
        committedSnapshot = shownData
        isDirty = false
        val availableRowIds = shownData.map { it.first }.toSet()
        selectedRows = selectedRows.filter { it in availableRowIds }.toSet()
        if (lastClickedRowIndex != null && lastClickedRowIndex !in shownData.indices) {
            lastClickedRowIndex = null
        }
    }
    LaunchedEffect(shownHeaders) {
        val existing = columnOrder.filter { it in shownHeaders }
        val nextOrder = existing + shownHeaders.filter { it !in existing }
        val leading = fixedLeadingColumns.filter { it in shownHeaders }
        val trailing = fixedTrailingColumns.filter { it in shownHeaders && it !in leading }
        columnOrder = leading + nextOrder.filter { it !in leading && it !in trailing } + trailing
        columnVisibility = shownHeaders.associateWith { columnVisibility[it] ?: true }.toMutableMap()
    }
    LaunchedEffect(shownHeaders, filterProfiles, selectedFilterProfile) {
        val hasSavedSearch = FrameworkUserSettingsService.getUserColumnSettings(screenKey)
            ?.settingsWerte?.keys?.any { it.startsWith(TABULAR_FILTER_SETTING_PREFIX) } == true
        selectedFilterProfile?.takeUnless { hasSavedSearch }?.let { profileName ->
            val activeProfile = filterProfiles[profileName].orEmpty()
            shownHeaders.forEachIndexed { index, header ->
                filterTexts.getOrNull(index)?.value = activeProfile[header].orEmpty()
            }
        }
    }
    LaunchedEffect(screenKey, useUserSettings, userSettingsVersion) {
        if (screenKey.isNotBlank() && useUserSettings) {
            FrameworkUserSettingsService.ensureCurrentUserSettingsLoaded(forceReload = userSettingsVersion > 0L)
            FrameworkUserSettingsService.getUserColumnSettings(screenKey)?.let { settings ->
                val legacyToId = headerLabelMap.entries.associate { (id, label) -> label to id }
                val savedColumns = settings.columns.map { it.takeIf { key -> key in shownHeaders } ?: legacyToId[it] }
                    .filterNotNull().distinct().filter { it in shownHeaders }
                val restoredOrder = savedColumns + shownHeaders.filter { it !in savedColumns }
                val leading = fixedLeadingColumns.filter { it in shownHeaders }
                val trailing = fixedTrailingColumns.filter { it in shownHeaders && it !in leading }
                columnOrder = leading + restoredOrder.filter { it !in leading && it !in trailing } + trailing
                val hidden = settings.hidden.map { it.takeIf { key -> key in shownHeaders } ?: legacyToId[it] }.filterNotNull().toSet()
                columnVisibility = shownHeaders.associateWith { it !in hidden }.toMutableMap()
                visibleRowsCount = settings.visibleLimit.coerceAtLeast(1)
                settings.settingsWerte.forEach { (key, value) ->
                    if (key.startsWith(TABULAR_FILTER_SETTING_PREFIX)) {
                        val header = key.removePrefix(TABULAR_FILTER_SETTING_PREFIX)
                        val index = shownHeaders.indexOf(header)
                        filterTexts.getOrNull(index)?.value = value
                    }
                }
            }
            FrameworkUserSettingsService.getUserCsvImportSettings(screenKey)?.let { importSettings ->
                csvImportProfiles = importSettings.profiles
                selectedCsvImportProfile = importSettings.selectedProfile
                    .takeIf { it.isNotBlank() && csvImportProfiles.containsKey(it) }
            } ?: run {
                csvImportProfiles = emptyMap()
                selectedCsvImportProfile = null
            }
            FrameworkUserSettingsService.getUserCsvExportSettings(screenKey)?.let { exportSettings ->
                csvExportProfiles = exportSettings.profiles
                selectedCsvExportProfile = exportSettings.selectedProfile
                    .takeIf { it.isNotBlank() && csvExportProfiles.containsKey(it) }
            } ?: run {
                csvExportProfiles = emptyMap()
                selectedCsvExportProfile = null
            }
            FrameworkUserSettingsService.getUserFilterProfileSettings(screenKey)?.let { filterSettings ->
                filterProfiles = filterSettings.profiles
                selectedFilterProfile = filterSettings.selectedProfile
                    .takeIf { it.isNotBlank() && filterProfiles.containsKey(it) }
            } ?: run {
                filterProfiles = emptyMap()
                selectedFilterProfile = null
            }
        }
    }

    fun markDirty() { isDirty = true }
    val resolvedEntityScope = remember(screenKey, entityScope) {
        entityScope.ifBlank { screenKey }
    }
    fun persistColumnSettings() {
        if (screenKey.isBlank() || !useUserSettings) return
        scope.launch {
            FrameworkUserSettingsService.saveCurrentUserColumnSettings(
                screenKey = screenKey,
                order = columnOrder,
                hidden = columnVisibility.filter { !it.value }.keys,
                limit = visibleRowsCount
            )
        }
    }
    fun persistCsvImportSettings() {
        if (screenKey.isBlank() || !useUserSettings) return
        scope.launch {
            FrameworkUserSettingsService.saveCurrentUserCsvImportSettings(
                screenKey = screenKey,
                profiles = csvImportProfiles,
                selectedProfile = selectedCsvImportProfile
            )
        }
    }
    fun persistCsvExportSettings() {
        if (screenKey.isBlank() || !useUserSettings) return
        scope.launch {
            FrameworkUserSettingsService.saveCurrentUserCsvExportSettings(
                screenKey = screenKey,
                profiles = csvExportProfiles,
                selectedProfile = selectedCsvExportProfile
            )
        }
    }
    fun persistFilterProfileSettings() {
        if (screenKey.isBlank() || !useUserSettings) return
        scope.launch {
            FrameworkUserSettingsService.saveCurrentUserFilterProfileSettings(
                screenKey = screenKey,
                profiles = filterProfiles,
                selectedProfile = selectedFilterProfile
            )
        }
    }
    fun clearAllFilters() {
        filterTexts.forEach { it.value = "" }
    }

    // ===================== COLUMN SETTINGS =====================
    val onColumnVisibilityChange: (String, Boolean) -> Unit = { header, checked ->
        columnVisibility = columnVisibility.toMutableMap().apply { this[header] = checked }
        persistColumnSettings()
    }
    val onColumnOrderChange: (String, String) -> Unit = { dragged, target ->
        val from = columnOrder.indexOf(dragged)
        val to = columnOrder.indexOf(target)
        if (from != -1 && to != -1 && from != to) {
            val newOrder = columnOrder.toMutableList().also { it.removeAt(from); it.add(to, dragged) }
            val leading = fixedLeadingColumns.filter { it in newOrder }
            val trailing = fixedTrailingColumns.filter { it in newOrder && it !in leading }
            columnOrder = leading + newOrder.filter { it !in leading && it !in trailing } + trailing
            persistColumnSettings()
        }
    }

    LaunchedEffect(screenKey, shownHeaders, visibleColumns) {
        if (!recoveredHiddenColumns && shownHeaders.isNotEmpty() && visibleColumns.isEmpty()) {
            recoveredHiddenColumns = true
            columnVisibility = shownHeaders.associateWith { true }.toMutableMap()
            columnOrder = shownHeaders
            persistColumnSettings()
        }
    }

    LaunchedEffect(screenKey, shownData, visibleRowsCount) {
        if (shownData.isNotEmpty() && visibleRowsCount < 1) {
            visibleRowsCount = MAX_VISIBLE_ROWS
            persistColumnSettings()
        }
    }

    LaunchedEffect(screenKey, shownData, filteredDataSize, selectedFilterProfile) {
        val hasActiveFilters = activeFilterTexts.any { it.value.isNotBlank() }
        // Only recover an invalid persisted profile. A manually typed filter may
        // legitimately have no matches and must not be erased while typing.
        if (!recoveredEmptyFilters && selectedFilterProfile != null && shownData.isNotEmpty() && filteredDataSize == 0 && hasActiveFilters) {
            recoveredEmptyFilters = true
            clearAllFilters()
            selectedFilterProfile = null
            persistFilterProfileSettings()
        }
    }

    // ===================== SORT =====================
    val onSortChange: (String) -> Unit = { column ->
        if (sortColumn == column) {
            if (sortAscending) sortAscending = false
            else { sortColumn = null; sortAscending = true }
        } else {
            sortColumn = column
            sortAscending = true
        }
    }

    // ===================== SELECTION =====================
    val onRowSelect: (String, Int, Boolean, Boolean) -> Unit = { id, index, shift, ctrl ->
        when {
            ctrl -> selectedRows = if (selectedRows.contains(id)) selectedRows - id else selectedRows + id
            shift && lastClickedRowIndex != null -> {
                val start = minOf(index, lastClickedRowIndex!!)
                val end = maxOf(index, lastClickedRowIndex!!)
                selectedRows = selectedRows + renderedData.subList(start, end + 1).map { it.first }.toSet()
            }
            else -> selectedRows = if (id.isEmpty()) emptySet() else setOf(id)
        }
        lastClickedRowIndex = if (id.isEmpty()) null else index
    }

    // ===================== ENGINE ACTIONS (RAM ONLY) =====================

    val createHandler: () -> Unit = {
        val maxId = shownData.maxOfOrNull { (id, _) -> id.toIntOrNull() ?: 0 } ?: 0
        val newId = (maxId + 1).toString()
        val row = MutableList(shownHeaders.size) { idx -> createRow(newId).getOrNull(idx) ?: "" }
        if (shownData.none { it.first == newId }) {
            shownData = shownData + (newId to row)
            markDirty()
            onDraftChange?.invoke(shownData)
        }
    }

    val deleteHandler: (List<String>) -> Unit = { ids ->
        if (ids.isNotEmpty()) {
            val previousData = shownData
            val previousSelection = selectedRows
            shownData = shownData.filterNot { it.first in ids }
            onDraftChange?.invoke(shownData)
            selectedRows = emptySet()
            if (onDeleteRows != null) {
                scope.launch {
                    try {
                        GlobalManager.markLocalTabularCommit(screenKey)
                        onDeleteRows(ids)
                        committedSnapshot = shownData
                        isDirty = false
                        if (resolvedEntityScope.isNotBlank()) {
                        }
                    } catch (cancellation: CancellationException) {
                        throw cancellation
                    } catch (error: Throwable) {
                        shownData = previousData
                        selectedRows = previousSelection
                        AppDialogs.alert(error.message ?: "Löschen fehlgeschlagen. Änderungen wurden zurückgesetzt.")
                    }
                }
            } else {
                markDirty()
            }
        }
    }

    // Zentraler RAM-Edit-Handler.
    // Erkennt ✅/⬜ automatisch als Toggle - kein onCellClick-Domain-Callback nötig.
    val cellEditHandler: (String, Int, String) -> Unit = { id, col, newValue ->
        val idx = shownData.indexOfFirst { it.first == id }
        if (idx >= 0) {
            val row = shownData[idx].second.toMutableList().also { r ->
                while (r.size < shownHeaders.size) r.add("")
                if (col in r.indices) {
                    r[col] = newValue
                }
            }
            val resolvedColName = shownHeaders.getOrNull(col).orEmpty()
            val resolvedValue = row.getOrNull(col).orEmpty()
            val transformedRow = onCellEditTransform
                ?.invoke(id, resolvedColName, resolvedValue, row.toList())
                ?.toMutableList()
                ?: row
            if (transformedRow != shownData[idx].second) {
                shownData = shownData.toMutableList().also { it[idx] = id to transformedRow }
                markDirty()
                onDraftChange?.invoke(shownData)
                onCellChanged?.invoke(id, resolvedColName, resolvedValue)
            }
        }
    }

    LaunchedEffect(bulkEditRequest?.nonce) {
        bulkEditRequest?.let { request ->
            val columnIndex = shownHeaders.indexOf(request.column)
            if (columnIndex >= 0) request.rowIds.forEach { rowId ->
                cellEditHandler(rowId, columnIndex, request.value)
            }
        }
    }

    // Einziger Speicher-Trigger - alles andere ist RAM.
    var commitPending by remember { mutableStateOf(false) }
    val saveAllHandler: () -> Unit = {
        if (isDirty && !commitPending) {
            val before = committedSnapshot.toMap()
            val after = shownData.toMap()
            val upserts = after.keys.filter { id ->
                before[id] != after[id]
            }
            val deletes = before.keys.filter { id ->
                id !in after
            }
            GlobalManager.markLocalTabularCommit(screenKey)
            val commitRows = if (commitChangedRowsOnly) {
                shownData.filter { (id, _) -> id in upserts }
            } else {
                shownData
            }
            if (onCommitConfirmed != null) {
                val submitted = shownData.map { it.first to it.second.toMutableList() }
                commitPending = true
                onCommitConfirmed(commitRows) { success ->
                    commitPending = false
                    if (success) {
                        committedSnapshot = submitted
                        isDirty = shownData != submitted
                    }
                }
            } else {
                onCommit(commitRows)
                committedSnapshot = shownData
                isDirty = false
            }
        }
    }

    val onCsvImportHandler: (String) -> Unit = { csv ->
        val rows = parseSemicolonCsv(csv).filter { row -> row.any { it.isNotBlank() } }
        if (rows.isEmpty()) {
            AppDialogs.alert("Leere CSV-Datei.")
        } else {
            val csvHeaders = rows.first().map { it }
            val activeProfileMap = selectedCsvImportProfile?.let { csvImportProfiles[it] }.orEmpty()

            val headerMapping = mutableMapOf<Int, Int>()
            shownHeaders.forEachIndexed { tableIndex, tableHeader ->
                val displayLabel = headerLabelMap[tableHeader].orEmpty().ifBlank { tableHeader }
                val alias = activeProfileMap[tableHeader] ?: activeProfileMap[displayLabel].orEmpty()
                val csvIndex = csvHeaders.indexOfFirst { csvHeader ->
                    csvHeader.equals(tableHeader, ignoreCase = true) ||
                        csvHeader.equals(displayLabel, ignoreCase = true) ||
                        (alias.isNotBlank() && csvHeader.equals(alias, ignoreCase = true))
                }
                if (csvIndex in csvHeaders.indices) headerMapping[csvIndex] = tableIndex
            }

            val existingIds = shownData.map { it.first }.toSet()
            var importIndex = 0
            val imported = rows.drop(1).mapNotNull { values ->
                val aligned = MutableList(shownHeaders.size) { "" }
                values.forEachIndexed { csvIdx, v ->
                    headerMapping[csvIdx]?.let { tableIdx -> aligned[tableIdx] = v }
                }
                if (shownData.any { it.second == aligned }) return@mapNotNull null
                var newId: String
                do { newId = "import_${kotlin.js.Date().getTime().toLong()}_${importIndex++}" }
                while (newId in existingIds)
                newId to aligned
            }

            if (imported.isEmpty()) {
                AppDialogs.alert("Keine neuen Zeilen zum Importieren.")
            } else {
                shownData = shownData + imported
                onDraftChange?.invoke(shownData)
                AppDialogs.alert("${imported.size} Zeilen importiert.")
                markDirty()
            }
        }
    }

    val onCsvImportProfileSelectHandler: (String?) -> Unit = { profileName ->
        selectedCsvImportProfile = profileName?.takeIf { it.isNotBlank() && csvImportProfiles.containsKey(it) }
        persistCsvImportSettings()
    }

    val onCsvImportProfileSaveHandler: (String, Map<String, String>) -> Unit = { profileName, mapping ->
        csvImportProfiles = csvImportProfiles.toMutableMap().apply {
            this[profileName] = mapping
        }
        selectedCsvImportProfile = profileName
        persistCsvImportSettings()
    }

    val onCsvImportProfileDeleteHandler: (String) -> Unit = { profileName ->
        csvImportProfiles = csvImportProfiles.toMutableMap().apply { remove(profileName) }
        if (selectedCsvImportProfile == profileName) selectedCsvImportProfile = null
        persistCsvImportSettings()
    }

    val onCsvExportProfileSelectHandler: (String?) -> Unit = { profileName ->
        selectedCsvExportProfile = profileName?.takeIf { it.isNotBlank() && csvExportProfiles.containsKey(it) }
        persistCsvExportSettings()
    }

    val onCsvExportProfileSaveHandler: (String, Map<String, String>) -> Unit = { profileName, mapping ->
        csvExportProfiles = csvExportProfiles.toMutableMap().apply {
            this[profileName] = mapping
        }
        selectedCsvExportProfile = profileName
        persistCsvExportSettings()
    }

    val onCsvExportProfileDeleteHandler: (String) -> Unit = { profileName ->
        csvExportProfiles = csvExportProfiles.toMutableMap().apply { remove(profileName) }
        if (selectedCsvExportProfile == profileName) selectedCsvExportProfile = null
        persistCsvExportSettings()
    }

    val onFilterProfileSelectHandler: (String?) -> Unit = { profileName ->
        selectedFilterProfile = profileName?.takeIf { it.isNotBlank() && filterProfiles.containsKey(it) }
        val activeProfile = selectedFilterProfile?.let { filterProfiles[it] }.orEmpty()
        shownHeaders.forEachIndexed { index, header ->
            val value = activeProfile[header].orEmpty()
            filterTexts.getOrNull(index)?.value = value
        }
        persistFilterProfileSettings()
    }

    val onFilterProfileSaveHandler: (String, Map<String, String>) -> Unit = { profileName, mapping ->
        filterProfiles = filterProfiles.toMutableMap().apply {
            this[profileName] = mapping
        }
        selectedFilterProfile = profileName
        shownHeaders.forEachIndexed { index, header ->
            val value = mapping[header].orEmpty()
            filterTexts.getOrNull(index)?.value = value
        }
        persistFilterProfileSettings()
    }

    val onFilterProfileDeleteHandler: (String) -> Unit = { profileName ->
        filterProfiles = filterProfiles.toMutableMap().apply { remove(profileName) }
        if (selectedFilterProfile == profileName) selectedFilterProfile = null
        persistFilterProfileSettings()
    }

    // ===================== UI CALLBACKS =====================
    val onColumnToggleToggle = { showColumnToggle = !showColumnToggle }
    val onColumnToggleClose = { showColumnToggle = false }

    val onRefreshExecute: () -> Unit = {
        val savedFilters = shownHeaders.mapIndexed { index, header ->
            "$TABULAR_FILTER_SETTING_PREFIX$header" to filterTexts.getOrNull(index)?.value.orEmpty()
        }.toMap()
        scope.launch {
            if (screenKey.isNotBlank() && useUserSettings) {
                FrameworkUserSettingsService.saveCurrentUserColumnSettings(
                    screenKey = screenKey,
                    order = columnOrder,
                    hidden = columnVisibility.filter { !it.value }.keys,
                    limit = visibleRowsCount,
                    settingsWerte = savedFilters
                )
            }
            // Prefer an in-place data refresh so filter inputs remain mounted.
            if (onRefresh != null) onRefresh() else reloadScreen?.invoke()
        }
    }

    val onSearchExecute: () -> Unit = {
        selectedRows = emptySet()
        val searchFilters = shownHeaders.mapIndexedNotNull { index, header ->
                filterTexts.getOrNull(index)?.value?.trim()?.takeIf { it.isNotEmpty() }?.let {
                    (headerLabelMap[header]?.takeIf { label -> label.isNotBlank() } ?: header) to it
                }
            }.toMap()
        val savedFilters = shownHeaders.mapIndexed { index, header ->
            "$TABULAR_FILTER_SETTING_PREFIX$header" to filterTexts.getOrNull(index)?.value.orEmpty()
        }.toMap()
        scope.launch {
            if (screenKey.isNotBlank() && useUserSettings) {
                FrameworkUserSettingsService.saveCurrentUserColumnSettings(
                    screenKey = screenKey,
                    order = columnOrder,
                    hidden = columnVisibility.filter { !it.value }.keys,
                    limit = visibleRowsCount,
                    settingsWerte = savedFilters
                )
            }
            onSearch?.invoke()
            onSearchWithFilters?.invoke(searchFilters)
        }
        Unit
    }

    val onLoadMore: () -> Unit = {
        visibleRowsCount += step
        persistColumnSettings()
    }

    val onLimitChange: (Int) -> Unit = { newLimit ->
        visibleRowsCount = newLimit.coerceAtLeast(1)
        persistColumnSettings()
    }

    // ===================== RENDER =====================
    // Stufenmodell (leer = keine Einschränkung):
    //   LESEN     -> nur anzeigen
    //   SCHREIBEN -> erstellen, bearbeiten, speichern
    //   ALLES     -> + löschen, stornieren, Passwort-Reset
    val canEdit = !hardReadOnly && (rollenKey.isBlank() || RollenService.hasActionPermission(rollenKey, RollenService.ACTION_EDIT))
    val canSave = !hardReadOnly && (rollenKey.isBlank() || RollenService.hasActionPermission(rollenKey, RollenService.ACTION_SAVE))
    val canCreate = !hardReadOnly && (rollenKey.isBlank() || RollenService.hasActionPermission(rollenKey, RollenService.ACTION_CREATE))
    val canDelete = !hardReadOnly && (rollenKey.isBlank() || RollenService.hasActionPermission(rollenKey, RollenService.ACTION_DELETE))
    val canImport = !hardReadOnly && (rollenKey.isBlank() || RollenService.hasActionPermission(rollenKey, RollenService.ACTION_IMPORT))
    val canExport = rollenKey.isBlank() || RollenService.hasActionPermission(rollenKey, RollenService.ACTION_EXPORT)
    val canSettings = rollenKey.isBlank() || RollenService.hasActionPermission(rollenKey, RollenService.ACTION_SETTINGS)
    val canInstall = !hardReadOnly && (rollenKey.isBlank() || RollenService.hasActionPermission(rollenKey, RollenService.ACTION_INSTALL))
    val canCancel = !hardReadOnly && (rollenKey.isBlank() || RollenService.hasActionPermission(rollenKey, RollenService.ACTION_CANCEL))
    val canPassword = !hardReadOnly && (rollenKey.isBlank() || RollenService.hasActionPermission(rollenKey, RollenService.ACTION_PASSWORD))
    val canApprove = !hardReadOnly && (rollenKey.isBlank() || RollenService.hasActionPermission(rollenKey, RollenService.ACTION_APPROVE))
    val canGenerate = !hardReadOnly && (rollenKey.isBlank() || RollenService.hasActionPermission(rollenKey, RollenService.ACTION_GENERATE))

    TabularTheme.TabularLayout(
        title = title,
        allHeaders = shownHeaders,
        headerDisplayLabels = headerLabelMap,
        visibleColumns = visibleColumns,
        renderedData = renderedData,
        filteredDataSize = filteredDataSize,
        headerNameToIndex = headerNameToIndex,
        nonEditableColIndices = nonEditableColIndices,
        toggleColIndices = toggleColIndices,
        dropdownOptionIndices = dropdownOptionIndices,
        rowDropdownOptions = rowDropdownOptions,
        calendarColumnIndices = calendarColumnIndices,
        multilineColumnIndices = multilineColumns.mapNotNull { headerNameToIndex[it] }.toSet(),
        cellBackgroundColors = cellBackgroundColors,
        editable = editable && canEdit,
        cellEditable = cellEditable,
        selectionMode = selectionMode,
        createButtonLabel = createButtonLabel,
        snapButtonLabel = snapButtonLabel,
        saveButtonLabel = saveButtonLabel,
        importButtonLabel = importButtonLabel,
        exportButtonLabel = exportButtonLabel,
        exportFileName = exportFileName,
        csvImportAccept = csvImportAccept,
        toolbarMode = toolbarMode,
        generateButtonLabel = generateButtonLabel,
        remainingResultsCount = filteredDataSize - visibleRowsCount,
        loadedDataCount = minOf(visibleRowsCount, shownData.size),
        totalDataCount = shownData.size,
        selectedRows = selectedRows,
        selectedRowIndices = selectedRowIndices,
        selectedCell = selectedCell,
        selectedCellRange = selectedCellRange,
        filterTexts = filterTexts,
        filterToggleHeaders = toggleColumns,
        columnVisibility = columnVisibility,
        columnOrder = columnOrder,
        visibleRowsCount = visibleRowsCount,
        showColumnToggle = showColumnToggle,
        hoveredColIndex = null,
        onHeaderClick = onHeaderClick,
        onHeaderLabelEdit = onHeaderLabelEdit,
        onHoverColumnChange = {},
        sortColumn = sortColumn,
        sortAscending = sortAscending,
        onSortChange = onSortChange,
        onCreate = if (canCreate && allowCreate) (onCreateAction ?: createHandler) else null,
        onSnapAction = if (canCreate) onSnapAction else null,
        onCellEdit = if (canEdit) { id, col, value ->
            cellEditHandler(id, col, value)
        } else null,
        onSaveAll = if (canSave && allowSave) {
            if (onSaveRequest != null) {
                { onSaveRequest(saveAllHandler) }
            } else {
                saveAllHandler
            }
        } else null,
        onCsvImport = if (canImport && allowImportButton) (onCsvImportRequest ?: onCsvImportHandler) else null,
        allowImportButton = canImport && allowImportButton,
        allowExportButton = canExport && allowExportButton,
        allowSettingsButton = canSettings && allowSettingsButton,
        allowRefreshButton = allowRefreshButton && (reloadScreen != null || onRefresh != null),
        allowSearchButton = allowSearchButton,
        showToolbarExtras = showToolbarExtras,
        showFilterRow = showFilterRow,
        showFillerRows = showFillerRows,
        smartMode = smartMode,
        dataColumnWidthCss = dataColumnWidthCss,
        verticalScroll = verticalScroll,
        onGenerate = if (canGenerate) onGenerate else null,
        onDeleteMultiple = if (canDelete && allowDelete) { ids -> deleteHandler(ids) } else null,
        isDeleteEnabled = isDeleteEnabled,
        onInstallSelected = if (canInstall && onInstallSelected != null) {
            { ids ->
                // Installation persists the current row draft. Allow the resulting
                // backend/model state (including a changed row ID) to replace it.
                isDirty = false
                committedSnapshot = shownData
                onInstallSelected(ids)
            }
        } else null,
        isInstallEnabled = isInstallEnabled,
        onCancelRows = if (canCancel) onCancelRows else null,
        isCancelEnabled = isCancelEnabled,
        onResetPassword = if (canPassword) onResetPassword else null,
        onApproveSelected = if (canApprove) onApproveSelected else null,
        approveButtonLabel = approveButtonLabel,
        installButtonLabel = installButtonLabel,
        cancelButtonLabel = cancelButtonLabel,
        cancelButtonIcon = cancelButtonIcon,
        extraNavButtons = extraNavButtons,
        onRowClick = onRowClick,
        onRowDoubleClick = onRowDoubleClick,
        onCellClick = onCellClick,
        onCellSelectionChange = onCellSelectionChange,
        onFilterClear = {
            clearAllFilters()
            selectedRows = emptySet()
        },
        onColumnToggleClose = onColumnToggleClose,
        onColumnToggleVisibilityChange = onColumnVisibilityChange,
        onLoadMore = onLoadMore,
        onLimitChange = onLimitChange,
        onRowSelect = onRowSelect,
        onRowIndexSelect = onRowIndexSelect,
        onRowIndexContextMenu = onRowIndexContextMenu,
        onColumnOrderChange = onColumnOrderChange,
        onColumnToggleToggle = onColumnToggleToggle,
        extraButtons = extraButtons,
        onRefreshExecute = onRefreshExecute,
        onSearchExecute = onSearchExecute,
        onCellContextMenu = onCellContextMenu,
        csvImportProfiles = csvImportProfiles,
        selectedCsvImportProfile = selectedCsvImportProfile,
        onCsvImportProfileSelect = onCsvImportProfileSelectHandler,
        onCsvImportProfileSave = onCsvImportProfileSaveHandler,
        onCsvImportProfileDelete = onCsvImportProfileDeleteHandler,
        csvExportProfiles = csvExportProfiles,
        selectedCsvExportProfile = selectedCsvExportProfile,
        onCsvExportProfileSelect = onCsvExportProfileSelectHandler,
        onCsvExportProfileSave = onCsvExportProfileSaveHandler,
        onCsvExportProfileDelete = onCsvExportProfileDeleteHandler,
        filterProfiles = filterProfiles,
        selectedFilterProfile = selectedFilterProfile,
        onFilterProfileSelect = onFilterProfileSelectHandler,
        onFilterProfileSave = onFilterProfileSaveHandler,
        onFilterProfileDelete = onFilterProfileDeleteHandler
    )
}
